import {
  useGetPopularSearchByIdQuery,
  useUpdatePopularSearchMutation,
} from "@/app/features/popularSearch/popularSearchApiSlice";
import FormImageInput from "@/components/forms/FormImageInput";
import FormInput from "@/components/forms/FormInput";
import FormSelect from "@/components/forms/FormSelect";
import FormTextArea from "@/components/forms/FormTextArea";
import {
  Box,
  Button,
  Container,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import * as Yup from "yup";

const PopularSearchesTypeEnum = {
  LIFESTYLE: "Lifestyle",
  CATEGORY: "Category",
};

const popularSearchTypes = [
  {
    code: PopularSearchesTypeEnum.CATEGORY,
    name: PopularSearchesTypeEnum.CATEGORY,
  },
  {
    code: PopularSearchesTypeEnum.LIFESTYLE,
    name: PopularSearchesTypeEnum.LIFESTYLE,
  },
];

const validationSchema = Yup.object().shape({
  name: Yup.string().required().min(2).max(50).label("Name").trim(),
  description: Yup.string()
    .required()
    .min(5)
    .max(500)
    .label("Description")
    .trim(),
  type: Yup.string()
    .required()
    .oneOf(["Category", "Lifestyle"], "Type must be Category or Lifestyle")
    .label("Type")
    .trim(),

  car_1: Yup.string().required().min(1).label("Car 1").trim(),
  car_2: Yup.string().required().min(1).label("Car 2").trim(),
  car_3: Yup.string().required().min(1).label("Car 3").trim(),
  car_4: Yup.string().required().min(1).label("Car 4").trim(),
  car_5: Yup.string().required().min(1).label("Car 5").trim(),
  car_6: Yup.string().required().min(1).label("Car 6").trim(),

  image: Yup.mixed()
    .nullable(true)
    .when("type", {
      is: "Category",
      then: (schema) =>
        schema
          .test(
            "image-required-for-category",
            "Image is required for Category",
            function (value) {
              // Accept either an existing image string or a file object
              const isFile = value && value.type;
              const isString = typeof value === "string" && value.trim() !== "";
              return isFile || isString;
            }
          )
          .test(
            "file-type-validation",
            "Only the following formats are accepted: .jpg, .jpeg, .png, .webp",
            function (value) {
              if (!value || typeof value === "string") return true;
              return [
                "image/png",
                "image/jpg",
                "image/jpeg",
                "image/webp",
              ].includes(value.type);
            }
          ),
      otherwise: (schema) => schema.notRequired(),
    })
    .label("Image"),
});

export default function ViewPopularSearchPage() {
  const params = useParams();
  const { data, isLoading, isError, error } = useGetPopularSearchByIdQuery(
    params.id
  );

  const navigate = useNavigate();

  const [updatePopularSearch, { isLoading: isUpdating }] =
    useUpdatePopularSearchMutation();

  const handleSubmit = async (inputs) => {
    try {
      const formData = new FormData();
      formData.append("name", inputs.name);
      formData.append("description", inputs.description);
      formData.append("type", inputs.type);
      formData.append("car_1", inputs.car_1.trim());
      formData.append("car_2", inputs.car_2.trim());
      formData.append("car_3", inputs.car_3.trim());
      formData.append("car_4", inputs.car_4.trim());
      formData.append("car_5", inputs.car_5.trim());
      formData.append("car_6", inputs.car_6.trim());

      if (inputs.image && inputs.type === "Category") {
        formData.append("image", inputs.image); // new image
      } else if (inputs.previewImage) {
        formData.append("image", inputs.previewImage); // send existing filename
      }

      const result = await updatePopularSearch({
        id: params.id,
        data: formData,
      });

      if (result && result.data) {
        toast.success(result.data.message);
        navigate("/popular-search");
      } else if (result && result.error) {
        toast.error(result.error.data.message);
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    }
  };

  const popularSearch = data?.data?.data;

  if (isLoading) {
    return (
      <Skeleton
        sx={{
          transform: "scale(1, 1.0)",
          transformOrigin: "0% 0%",
        }}
        width="100%"
        height="600px"
      />
    );
  }

  if (isError) {
    return <p>{error?.data?.message || "Something went wrong"}</p>;
  }

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        py: 8,
      }}
    >
      <Container maxWidth="md">
        <Stack spacing={3}>
          <Stack direction="row" justifyContent="space-between" spacing={4}>
            <Typography variant="h4">Edit Popular Search</Typography>
          </Stack>

          <Formik
            initialValues={{
              name: popularSearch?.name || "",
              description: popularSearch?.description || "",
              type: popularSearch?.type,
              car_1: popularSearch?.car_1 || "",
              car_2: popularSearch?.car_2 || "",
              car_3: popularSearch?.car_3 || "",
              car_4: popularSearch?.car_4 || "",
              car_5: popularSearch?.car_5 || "",
              car_6: popularSearch?.car_6 || "",
              image: popularSearch?.image,
              previewImage: popularSearch?.image || null,
            }}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
          >
            {({
              setFieldValue,
              values,
              setFieldTouched,
              setFieldError,
              errors,
            }) => (
              <Form>
                <Stack spacing={3}>
                  <FormInput
                    label="Name"
                    name="name"
                    type="text"
                    placeholder="Enter Name"
                  />
                  <FormSelect
                    name="type"
                    label="Select Type"
                    items={popularSearchTypes}
                  />
                  <FormTextArea
                    label="Description"
                    name="description"
                    type="text"
                    placeholder="Enter Description"
                  />
                  <FormInput
                    label="Car 1"
                    name="car_1"
                    type="text"
                    placeholder="Enter Car 1"
                  />
                  <FormInput
                    label="Car 2"
                    name="car_2"
                    type="text"
                    placeholder="Enter Car 2"
                  />
                  <FormInput
                    label="Car 3"
                    name="car_3"
                    type="text"
                    placeholder="Enter Car 3"
                  />
                  <FormInput
                    label="Car 4"
                    name="car_4"
                    type="text"
                    placeholder="Enter Car 4"
                  />
                  <FormInput
                    label="Car 5"
                    name="car_5"
                    type="text"
                    placeholder="Enter Car 5"
                  />
                  <FormInput
                    label="Car 6"
                    name="car_6"
                    type="text"
                    placeholder="Enter Car 6"
                  />
                  {values.type === PopularSearchesTypeEnum.CATEGORY && (
                    <FormImageInput
                      name="image"
                      imageValues={values.previewImage}
                      onChangeSelectedImage={(event) => {
                        setFieldTouched(event.target.name);
                        setFieldValue(event.target.name, event.target.files[0]);
                      }}
                      onError={(message) => {
                        setFieldError("image", message);
                      }}
                      onCheckRes={(checkResolution) => {
                        setFieldValue("checkResolution", checkResolution);
                      }}
                      checkResolution={values.checkResolution}
                      label="Image"
                      imageSrcFolderPath="popular-search/"
                    />
                  )}
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isUpdating}
                  >
                    Submit
                  </Button>
                </Stack>
              </Form>
            )}
          </Formik>
        </Stack>
      </Container>
    </Box>
  );
}
